import { Form, Formik } from 'formik';
import { MouseEvent, ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import Modal from '../../../../components/Modal/Modal';
import Panel from '../../../../components/Panel/Panel';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectSelectedDistrict } from '../../../../redux/slices/appSlice';
import { createNotification } from '../../../../redux/slices/notificationSlice';
import {
    createNeglectedOrDelinquentContact,
    fetchNeglectedOrDelinquentContact,
    fetchNeglectedOrDelinquentSchoolYears,
    updateNeglectedOrDelinquentContact,
} from '../../../../redux/thunks/neglectedOrDelinquentThunks';
import { appRoutePaths } from '../../../../services/route.service';
import { ButtonSize } from '../../../../types/ButtonSize';
import { DialogType } from '../../../../types/DialogTypes';
import { FeatureAccessPermissions } from '../../../../types/FeatureAccessPermissions';
import { NeglectedOrDelinquentContactModel } from '../../../../types/NeglectedOrDelinquentContactModel';
import { PanelType } from '../../../../types/PanelType';
import { NeglectedOrDelinquentContactValidationSchema as validationSchema } from '../../../../types/Validation/NeglectedOrDelinquentContactValidationSchema';
import { hasSpecifiedFeatureAccess } from '../../../../utilities/userUtilities';
import DistrictSchoolInfoPage from '../DistrictSchoolInfoPage';
import './NeglectedOrDelinquentList.css';

type ButtonMouseEvent = MouseEvent<HTMLButtonElement>;

const NeglectedOrDelinquentList = (): ReactElement => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedDistrict = useAppSelector(selectSelectedDistrict);
    const { contact, schoolYears, contactStatus, schoolYearsStatus } = useAppSelector(
        (state) => state.neglectedOrDelinquent
    );
    const [selectedYear, setSelectedYear] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const canEdit = hasSpecifiedFeatureAccess(
        FeatureAccessPermissions.NEGLECTED_OR_DELINQUENT_ACCESS
    );

    useEffect(() => {
        if (!selectedDistrict.districtId) return;
        setSelectedYear('');
        const contactRequest = dispatch(fetchNeglectedOrDelinquentContact());
        return () => {
            contactRequest.abort();
        };
    }, [dispatch, selectedDistrict.districtId]);

    useEffect(() => {
        const yearsRequest = dispatch(fetchNeglectedOrDelinquentSchoolYears());
        return () => {
            yearsRequest.abort();
        };
    }, [dispatch]);

    const effectiveYear = selectedYear || schoolYears[0]?.value || '';
    const initialValues: NeglectedOrDelinquentContactModel = contact || {
        id: '',
        leaIdentifier: selectedDistrict.districtId || '',
        firstName: '',
        lastOrSurname: '',
        position: '',
        email: '',
        telephone: '',
    };
    const editViewTooltip = contact?.id
        ? ['Edit/View Neglected or', 'Delinquent'].join(' ')
        : 'Save contact information first';
    const isLoading = contactStatus === 'idle' || contactStatus === 'pending';

    const handleEditViewClick = (): void => {
        navigate(appRoutePaths.NeglectedOrDelinquentInput(effectiveYear));
    };

    return (
        <DistrictSchoolInfoPage
            pageClass="data-input--district-school-info--neglected-or-delinquent--list"
            isLoading={isLoading}
            loadingDataId="neglected-or-delinquent-list-loader"
        >
            <div className="content-outer-container">
                <div className="content-inner-container">
                    <div className="subheading-and-button-container">
                        <h4>Neglected or Delinquent</h4>
                        <span className="nord-required">* Required Field</span>
                    </div>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        enableReinitialize
                        validateOnMount
                        onSubmit={async (values, { resetForm }) => {
                            const normalized = {
                                ...values,
                                leaIdentifier: selectedDistrict.districtId,
                                firstName: values.firstName.trim(),
                                lastOrSurname: values.lastOrSurname.trim(),
                                position: values.position.trim(),
                                email: values.email.trim(),
                            };
                            try {
                                const saved = await dispatch(
                                    values.id
                                        ? updateNeglectedOrDelinquentContact(normalized)
                                        : createNeglectedOrDelinquentContact(normalized)
                                ).unwrap();
                                resetForm({ values: saved });
                                dispatch(
                                    createNotification({
                                        severity: 'success',
                                        children: 'Neglected or Delinquent contact saved',
                                    })
                                );
                            } catch {
                                return;
                            }
                        }}
                    >
                        {({ dirty, isValid, isSubmitting, resetForm, submitForm }) => {
                            const disabled =
                                !canEdit || contactStatus !== 'succeeded' || isSubmitting;
                            const disableYearActions = dirty || isSubmitting;
                            return (
                                <Form>
                                    <Panel
                                        panelType={PanelType.INFO}
                                        heading={
                                            'Neglected or Delinquent ' +
                                            'LEA Contact Information'
                                        }
                                    >
                                        <div className="contacts-container">
                                            <div>
                                                <div className="field-row">
                                                    <FormTextbox
                                                        displayName="First Name"
                                                        field="firstName"
                                                        disabled={disabled}
                                                        validationSchema={
                                                            validationSchema
                                                        }
                                                    />
                                                    <FormTextbox
                                                        displayName="Last Name"
                                                        field="lastOrSurname"
                                                        disabled={disabled}
                                                        validationSchema={
                                                            validationSchema
                                                        }
                                                    />
                                                    <FormTextbox
                                                        displayName="Position"
                                                        field="position"
                                                        placeholder={
                                                            'Optional Input, ' +
                                                            'Not Required'
                                                        }
                                                        disabled={disabled}
                                                        validationSchema={
                                                            validationSchema
                                                        }
                                                    />
                                                </div>
                                                <br />
                                                <div className="field-row">
                                                    <FormTextbox
                                                        displayName="Email"
                                                        field="email"
                                                        disabled={disabled}
                                                        validationSchema={
                                                            validationSchema
                                                        }
                                                    />
                                                    <FormTextbox
                                                        displayName="Telephone Number"
                                                        field="telephone"
                                                        disabled={disabled}
                                                        validationSchema={
                                                            validationSchema
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-actions">
                                                <ActionButton
                                                    size={ButtonSize.SMALL}
                                                    buttonType="button"
                                                    dataTestId="cancel-button"
                                                    disabled={disabled || !dirty}
                                                    tooltipText="Cancel changes"
                                                    onClick={() =>
                                                        setShowCancelModal(true)
                                                    }
                                                >
                                                    Cancel
                                                </ActionButton>
                                                <ActionButton
                                                    size={ButtonSize.SMALL}
                                                    buttonType="submit"
                                                    classes="button--secondary"
                                                    dataTestId="submit-button"
                                                    disabled={
                                                        disabled || !dirty || !isValid
                                                    }
                                                    loading={isSubmitting}
                                                    tooltipText="Save contact information"
                                                    onClick={async (
                                                        event: ButtonMouseEvent
                                                    ) => {
                                                        event.preventDefault();
                                                        await submitForm();
                                                    }}
                                                >
                                                    Save
                                                </ActionButton>
                                            </div>
                                        </div>
                                    </Panel>
                                    <br />
                                    <div className="filters-selection-container">
                                        <div className="dropdown-selection">
                                            <StyledSingleDropdown
                                                fieldName="schoolYear"
                                                options={schoolYears}
                                                value={
                                                    schoolYears.find(
                                                        (year) =>
                                                            year.value === effectiveYear
                                                    )
                                                        ? {
                                                            value: effectiveYear,
                                                            label:
                                                                schoolYears.find(
                                                                    (year) =>
                                                                        year.value ===
                                                                        effectiveYear
                                                                )?.text || '',
                                                        }
                                                        : null
                                                }
                                                onChange={async (option) => {
                                                    setSelectedYear(option?.value || '');
                                                }}
                                                labelText="School Year"
                                                showLabel
                                                isClearable={false}
                                                isSearchable={false}
                                                isLoading={
                                                    schoolYearsStatus === 'pending'
                                                }
                                                disabled={
                                                    !contact?.id || disableYearActions
                                                }
                                                tooltipText="Select school year"
                                            />
                                        </div>
                                        <ActionButton
                                            buttonType="button"
                                            classes="no-wrap-text edit-view-button"
                                            dataTestId="edit-view-button"
                                            disabled={
                                                !contact?.id ||
                                                contactStatus !== 'succeeded' ||
                                                schoolYearsStatus !== 'succeeded' ||
                                                !effectiveYear ||
                                                disableYearActions
                                            }
                                            tooltipText={editViewTooltip}
                                            onClick={handleEditViewClick}
                                        >
                                            Edit/View
                                        </ActionButton>
                                        <div className="edit-view-helper-text">
                                            Click the Edit/View button to access the
                                            Neglected or Delinquent Information.
                                        </div>
                                    </div>
                                    {showCancelModal && (
                                        <Modal
                                            type={DialogType.CONFIRM}
                                            open={showCancelModal}
                                            title="Are you sure?"
                                            onClose={() => setShowCancelModal(false)}
                                        >
                                            <div
                                                className="confirm-container"
                                                data-testid="confirmation-modal"
                                            >
                                                <p data-testid="cancel-message">
                                                    Your changes have not been saved. Do
                                                    you wish to continue?
                                                </p>
                                                <div className="button-row">
                                                    <ActionButton
                                                        onClick={() => {
                                                            resetForm();
                                                            setShowCancelModal(false);
                                                        }}
                                                        dataTestId="yes-button"
                                                        tooltipText={
                                                            'Discard changes and ' +
                                                            'continue'
                                                        }
                                                    >
                                                        <span>Yes</span>
                                                    </ActionButton>
                                                    <ActionButton
                                                        classes="button--secondary"
                                                        onClick={() =>
                                                            setShowCancelModal(false)
                                                        }
                                                        dataTestId="no-button"
                                                        tooltipText="Stay on this page"
                                                    >
                                                        <span>No</span>
                                                    </ActionButton>
                                                </div>
                                            </div>
                                        </Modal>
                                    )}
                                </Form>
                            );
                        }}
                    </Formik>
                </div>
            </div>
        </DistrictSchoolInfoPage>
    );
};

export default NeglectedOrDelinquentList;
